// src/components/planner/modals/QuickStudyModal.tsx
// ✅ Tipos completos + suporte a "Outro" com customType

import React, { useState, useEffect } from 'react';
import { X, Play } from 'lucide-react';
import { STUDY_TYPES } from '../constants/studyTypes';

const API_BASE_URL = '/api';

interface QuickStudyModalProps {
    onClose: () => void;
    onStart: (type: string, subject?: string, customType?: string, color?: string) => void;
}

const QuickStudyModal: React.FC<QuickStudyModalProps> = ({ onClose, onStart }) => {
    const [type, setType] = useState('flashcards');
    const [subject, setSubject] = useState('');
    const [customType, setCustomType] = useState('');
    const [selectedColor, setSelectedColor] = useState('#6B7280');
    const [selectedChip, setSelectedChip] = useState('');
    const [savedCustomTypes, setSavedCustomTypes] = useState<{ name: string; color: string }[]>([]);

    const OTHER_COLORS = [
        '#f59e0b', '#84cc16', '#06b6d4', '#8b5cf6',
        '#f43f5e', '#14b8a6', '#fb923c', '#a78bfa',
    ];

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    setSavedCustomTypes(data.types || []);
                }
            } catch { }
        };
        load();
    }, []);

    const handleTypeChange = (newType: string) => {
        setType(newType);
        setCustomType('');
        setSelectedChip('');
        setSelectedColor('#6B7280');
    };

    const handleChipSelect = (ct: { name: string; color: string }) => {
        setSelectedChip(ct.name);
        setCustomType(ct.name);
        setSelectedColor(ct.color);
    };

    const handleCustomTypeInput = (val: string) => {
        setCustomType(val);
        setSelectedChip('');
    };

    const handleStart = () => {
        const ct = type === 'other' ? (customType.trim() || undefined) : undefined;
        const col = type === 'other' ? selectedColor : undefined;
        onStart(type, subject || undefined, ct, col);
    };

    const isOther = type === 'other';

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={e => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Iniciar Sessão</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="plnr-modal-body">
                    <div className="plnr-hint-box">
                        <p>Inicie agora. Você pode ajustar os detalhes depois.</p>
                    </div>

                    {/* TIPO DE ESTUDO */}
                    <div className="plnr-form-group">
                        <label>Tipo de estudo</label>
                        <select
                            value={type}
                            onChange={e => handleTypeChange(e.target.value)}
                            className="plnr-select"
                        >
                            {STUDY_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* CAMPOS DE "OUTRO" */}
                    {isOther && (
                        <>
                            {savedCustomTypes.length > 0 && (
                                <div className="plnr-form-group">
                                    <label>Tipos anteriores</label>
                                    <div className="plnr-custom-type-chips">
                                        {savedCustomTypes.map(ct => (
                                            <button
                                                key={ct.name}
                                                type="button"
                                                className={`plnr-custom-type-chip ${selectedChip === ct.name ? 'active' : ''}`}
                                                style={{
                                                    borderColor: ct.color,
                                                    backgroundColor: selectedChip === ct.name ? ct.color : 'transparent',
                                                    color: selectedChip === ct.name ? 'white' : ct.color,
                                                }}
                                                onClick={() => handleChipSelect(ct)}
                                            >
                                                {ct.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="plnr-form-group">
                                <label>
                                    {savedCustomTypes.length > 0 ? 'Ou digite um novo tipo (opcional)' : 'Nome do tipo (opcional)'}
                                </label>
                                <input
                                    type="text"
                                    value={customType}
                                    onChange={e => handleCustomTypeInput(e.target.value)}
                                    placeholder="Ex: Mapas Conceituais"
                                    className="plnr-input"
                                />
                            </div>

                            {/* Cor — só para tipo novo */}
                            {!selectedChip && (
                                <div className="plnr-form-group">
                                    <label>Cor</label>
                                    <div className="plnr-color-grid">
                                        {OTHER_COLORS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`plnr-color-option ${selectedColor === color ? 'active' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => setSelectedColor(color)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ASSUNTO */}
                    <div className="plnr-form-group">
                        <label>Assunto (opcional)</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder="Ex: Cardiologia"
                            className="plnr-input"
                            onKeyDown={e => e.key === 'Enter' && handleStart()}
                        />
                    </div>
                </div>

                <div className="plnr-modal-footer">
                    <button className="plnr-btn secondary" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="plnr-btn primary" onClick={handleStart}>
                        <Play size={16} />
                        Iniciar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuickStudyModal;